import { Observable, forkJoin, from, map } from "rxjs";

import { services } from "@progressive-development/pd-spa-helper";
import type { IDatabaseProvider } from "@progressive-development/pd-provider-interfaces";
import type { wahl } from "@progressive-development/politik-model";

import {
  COL_VERZEICHNISSE,
  DB_SHARED,
  DOK_BV_SITZVERTEILUNG,
  DOK_WAHLVERFAHREN_BV,
} from "../../../config/collections.js";

/**
 * Was NUR die Bezirksvertretung hat: die gerechnete Sitzverteilung samt dem
 * Verfahren, das sie erklärt. Sitzungen, Drucksachen und Kennzahlen liest sie
 * wie jedes Gremium über `modules/gremien/service/gremien-service.ts`.
 */
export class BvService {
  private get _db(): IDatabaseProvider {
    const db = services.database;
    if (!db) throw new Error("Kein Datenbank-Provider konfiguriert (provider-setup.ts).");
    return db;
  }

  private get _shared(): IDatabaseProvider {
    const db = services.databaseNamed(DB_SHARED);
    if (!db) throw new Error("Keine geteilte Datenbank konfiguriert (provider-setup.ts).");
    return db;
  }

  /**
   * Sitzverteilung und Wahlverfahren — zwei Dokumente aus ZWEI Datenbanken.
   *
   * Das Ergebnis ist Kölner Politik, das Verfahren ist Landesrecht: Ein Portal
   * einer anderen NRW-Stadt braucht dieselben Parameter. Sie kommen zusammen,
   * weil die Anzeige beides zugleich braucht — die Zahlen und den Satz, der
   * sie erklärt.
   */
  public ladeSitzverteilung(): Observable<BvSitze> {
    return forkJoin({
      verteilung: from(
        this._db.getDocument<wahl.BvSitzverteilungDokument>(
          COL_VERZEICHNISSE,
          DOK_BV_SITZVERTEILUNG,
        ),
      ).pipe(map((snap) => (snap.exists ? snap.data : undefined))),
      verfahren: from(
        this._shared.getDocument<wahl.WahlverfahrenDokument>(
          COL_VERZEICHNISSE,
          DOK_WAHLVERFAHREN_BV,
        ),
      ).pipe(map((snap) => (snap.exists ? snap.data : undefined))),
    });
  }
}

/** Was der Sitz-Block braucht: die Zahlen und die Parameter, die sie erklären. */
export interface BvSitze {
  verteilung?: wahl.BvSitzverteilungDokument;
  verfahren?: wahl.WahlverfahrenDokument;
}

export const bvService = new BvService();
